/**
 * Disbursement ledger (office staff / approvers / trustees). Financial-grade table:
 * monospaced figures, method icons, proof links, running totals header, method filter,
 * CSV export. Loading / empty / error states.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Landmark, Banknote, Building2, Download, RefreshCw, FileText, ArrowUpRight,
} from 'lucide-react';
import { api, getAccessToken } from '../lib/apiClient.js';
import { useToast } from '../components/feedback/ToastProvider.jsx';
import { formatINR, formatDate } from '../features/cases/caseMeta.js';
import styles from './Ledger.module.css';

const METHOD_META = {
  bank_transfer: { label: 'Bank transfer', icon: Landmark },
  manual_cash: { label: 'Manual cash', icon: Banknote },
  vendor_direct: { label: 'Vendor-direct', icon: Building2 },
};

export default function Ledger() {
  const toast = useToast();
  const [method, setMethod] = useState('');
  const [state, setState] = useState({ status: 'loading', items: [], totals: null, nextCursor: null, hasMore: false });
  const nextCursorRef = useRef(null);

  const load = useCallback(
    async (append = false) => {
      // Cursor via ref — a stale closure over state.nextCursor would make "Load more"
      // refetch page 1 and append duplicates.
      const cursor = append ? nextCursorRef.current : null;
      setState((s) => ({ ...s, status: append ? s.status : 'loading' }));
      try {
        const params = new URLSearchParams({ limit: '30' });
        if (method) params.set('method', method);
        if (cursor) params.set('cursor', cursor);
        const env = await api.getEnvelope(`/disbursements?${params}`);
        nextCursorRef.current = env.meta.page.nextCursor;
        setState((s) => ({
          status: 'ready',
          items: append ? [...s.items, ...env.data] : env.data,
          totals: env.meta.totals,
          nextCursor: env.meta.page.nextCursor,
          hasMore: env.meta.page.hasMore,
        }));
      } catch (err) {
        setState({ status: 'error', items: [], error: err.message });
      }
    },
    [method],
  );

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  const exportCsv = () => {
    const base = import.meta.env.VITE_API_BASE_URL || '/api';
    fetch(`${base}/disbursements/export`, { headers: { Authorization: `Bearer ${getAccessToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'disbursement-ledger.csv';
        a.click();
        toast.success('Ledger exported.');
      })
      .catch(() => toast.error('Export failed.'));
  };

  const t = state.totals;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          <p className="eyebrow">Casework</p>
          <h1 className={styles.h1}>Disbursement ledger</h1>
        </div>
        <button className={styles.ghost} onClick={exportCsv}>
          <Download size={15} /> Export CSV
        </button>
      </header>

      <div className={styles.totals}>
        <div className={styles.totalCard}>
          <span className={styles.totalLabel}>Total deployed</span>
          <span className={`${styles.totalValue} mono`}>{t ? formatINR(t.total) : '—'}</span>
          <span className={styles.totalSub}>{t ? `${t.count} disbursements` : ''}</span>
        </div>
        {Object.entries(METHOD_META).map(([k, m]) => (
          <div key={k} className={styles.totalCard}>
            <span className={styles.totalLabel}>
              <m.icon size={14} /> {m.label}
            </span>
            <span className={`${styles.totalValueSm} mono`}>{formatINR(t?.byMethod?.[k]?.total || 0)}</span>
            <span className={styles.totalSub}>{t?.byMethod?.[k]?.count || 0} entries</span>
          </div>
        ))}
      </div>

      <div className={styles.toolbar}>
        <select value={method} onChange={(e) => setMethod(e.target.value)} className={styles.select}>
          <option value="">All methods</option>
          {Object.entries(METHOD_META).map(([k, m]) => (
            <option key={k} value={k}>
              {m.label}
            </option>
          ))}
        </select>
        <button className={styles.ghost} onClick={() => load(false)}>
          <RefreshCw size={15} />
        </button>
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Date</th>
              <th>Case</th>
              <th>Method</th>
              <th>Vendor</th>
              <th className={styles.right}>Amount</th>
              <th>By</th>
              <th>Proof</th>
            </tr>
          </thead>
          <tbody>
            {state.status === 'loading' &&
              Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}>
                  <td colSpan={7}>
                    <div className={styles.skeleton} />
                  </td>
                </tr>
              ))}
            {state.status === 'error' && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  {state.error} — <button onClick={() => load(false)}>retry</button>
                </td>
              </tr>
            )}
            {state.status === 'ready' && state.items.length === 0 && (
              <tr>
                <td colSpan={7} className={styles.empty}>
                  No disbursements yet.
                </td>
              </tr>
            )}
            {state.status === 'ready' &&
              state.items.map((d) => {
                const M = METHOD_META[d.method]?.icon || Landmark;
                return (
                  <tr key={d.id}>
                    <td className={styles.sub}>{formatDate(d.at)}</td>
                    <td>
                      <Link to={`/cases/${d.case}`} className={styles.caseLink}>
                        <span className="mono">{d.caseNo}</span> <ArrowUpRight size={12} />
                      </Link>
                    </td>
                    <td>
                      <span className={styles.method}>
                        <M size={14} /> {METHOD_META[d.method]?.label}
                      </span>
                    </td>
                    <td className={styles.sub}>{d.vendorName || '—'}</td>
                    <td className={`${styles.right} ${styles.amount} mono`}>{formatINR(d.amount)}</td>
                    <td className={styles.sub}>{d.byName || '—'}</td>
                    <td>
                      {d.proofDoc ? (
                        <a href={d.proofDoc} target="_blank" rel="noreferrer" className={styles.proof}>
                          <FileText size={14} />
                        </a>
                      ) : d.txnRef ? (
                        <span className={styles.payoutId} title="Transaction reference">
                          {d.txnRef}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        {state.hasMore && state.status === 'ready' && (
          <button className={styles.loadMore} onClick={() => load(true)}>
            Load more
          </button>
        )}
      </div>
    </div>
  );
}
